import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import "../../developer/analytics.css";
import { CircularProgress, Backdrop, Box } from "@mui/material";
import { getConfig } from "../../../config.js";
import APIManager from "../../../Services/ApiManager/ApiManager.js";
import PopupDialog from "../../../Components/PopupDialog.jsx";
import { SideMenuReports } from "./sidemenu.jsx";

export default function Analytics({ initialNavItem, onNavigateToSummary }) {
  const [loader, setLoader] = useState(false);
  const [componentName, setComponentName] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const [activeNavItem, setActiveNavItem] = useState(initialNavItem);
  const [ticket, setTicket] = useState("");
  const [tableauRef, setTableauRef] = useState(null);
  const [tableauOpen, setTableauOpen] = useState(true);
  const tableauVizRef = useRef(null);

  const buildUrl = useCallback(() => {
    const portalConfig = sessionStorage.getItem("portalConfig");
    const parsedConfig = JSON.parse(portalConfig);
    if(parsedConfig.length<1)return"";
    else{
    const paths = parsedConfig[0].config.tableau.adminPaths;
    const url = parsedConfig[0].config.tableau.url;

    return (
      url + paths.ticket.replace("%ticket%", ticket) + paths[activeNavItem]
    );
  }
  }, [activeNavItem, ticket]);

  const initTableau = useCallback(() => {
    const embedUrl = buildUrl();
    if (!embedUrl || embedUrl.length < 1) {
      return;
    }

    if (tableauVizRef.current) {
      tableauVizRef.current.dispose();
    }

    const options = {
      width: "100%",
      height: "100%",
      hideTabs: true,
      hideToolbar: true,
      scrolling: "no",
      onFirstInteractive: function () {
        const workbookReports = tableauVizRef.current.getWorkbook();
        workbookReports.getActiveSheet();
      },
    };

    try {
      if (tableauRef && globalThis.tableau) {
        tableauVizRef.current = new globalThis.tableau.Viz(
          tableauRef,
          embedUrl,
          options
        );
      }
    } catch (e) {
      console.log("[WARNING!] Tableau embed exception:", e);
    }
  }, [buildUrl, tableauRef]);

  const handleTableauError = (error) => {
    console.error("Error fetching tableau ticket:", error);
    setComponentName("appError");
    setErrorMessage(error.message);
    setHeaderMessage(
      error.message === "jwt expired" ? "401 Token Expired" : "Error"
    );
    setOpenErrorPopup(true);
  };

  const refreshTableauTicket = async () => {
    setLoader(true);

    try {
      const isOemAdmin = getConfig().REACT_APP_PORTAL === "oem-admin";
      const response = isOemAdmin
        ? await APIManager.postAdminTableauUrl()
        : await APIManager.postAdminTableauUrlGobalAdmin();

      setTicket(response);

      return response;
    } catch (error) {
      handleTableauError(error);
      return null;
    } finally {
      setLoader(false);
    }
  };

  // Map item names to their display values
  const itemNameMap = {
    inAppPurchase: "In-app Purchase",
  };

  const handleActiveItemChange = async (itemName) => {
    if (itemName === "applicationSummary" && onNavigateToSummary) {
      onNavigateToSummary(itemName);
      return;
    }
    const newTicket = await refreshTableauTicket();
    if (!newTicket) return;

    // Use mapped value or default to itemName itself
    const displayValue = itemNameMap[itemName] || itemName;
    setActiveNavItem(displayValue);
  };

  useEffect(() => {
    refreshTableauTicket();
  }, []);

  useEffect(() => {
    if (ticket && ticket.length > 0 && activeNavItem) {
      initTableau();
    }

    return () => {
      if (tableauVizRef.current) {
        tableauVizRef.current.dispose();
      }
    };
  }, [ticket, activeNavItem, initTableau]);

  return (
    <>
      <Backdrop
        open={loader}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className="reports-side-menu">
        <SideMenuReports
          activeNavItem={activeNavItem}
          handleActiveItemChange={handleActiveItemChange}
          tableauOpen={tableauOpen}
          setTableauOpen={setTableauOpen}
        />
      </div>

      <Box
        className="application-summary-container"
        sx={{
          marginTop: "3%",
        }}
      >
        <div
          className="analytics-container-global-admin"
          style={{ width: "100%" }}
        >
          <div
            ref={(ref) => setTableauRef(ref)}
            style={{
              width: "100%",
              height: "100%",
              position: "relative",
            }}
          />
        </div>
      </Box>

      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage={headerMessage}
        componentName={componentName}
        onClose={() => setOpenErrorPopup(false)}
      />
    </>
  );
}

Analytics.propTypes = {
  initialNavItem: PropTypes.string,
  onNavigateToSummary: PropTypes.func,
};
